import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Nav,
  Navbar,
  Button,
  Dropdown,
} from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../../Images/AZ.png";
import { toast, ToastContainer } from 'react-toastify';
import axios from "axios";

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [profileColor, setProfileColor] = useState(null);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/our-story", label: "About" },
    { to: "/products", label: "Products" },
    { to: "/dbs-emi", label: "EMI" },
    { to: "/blog", label: "Blogs" },
  ];

  useEffect(() => {
    const stored = localStorage.getItem("userData");
    if (stored) {
      const user = JSON.parse(stored);
      setUserData(user);

      const userColorKey = `profileColor-${user.email_address}`;
      const userColor = localStorage.getItem(userColorKey);
      if (userColor) {
        setProfileColor(userColor);
      } else {
        const newColor = `hsl(${Math.floor(Math.random() * 360)}, 70%, 60%)`;
        localStorage.setItem(userColorKey, newColor);
        setProfileColor(newColor);
      }
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileNavOpen && navRef.current && !navRef.current.contains(event.target)) {
        setMobileNavOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileNavOpen]);

  const handleLogout = async () => {
    try {
      const res = await axios.post('https://www.azzenterprises.com/api/customers/signout', {}, { withCredentials: true });
      toast.success(res.data.message || 'Logged out successfully');
      setTimeout(() => {
        navigate("/sign-in");
        localStorage.removeItem("userData");
        if (userData && userData.email_address) {
          const userColorKey = `profileColor-${userData.email_address}`;
          localStorage.removeItem(userColorKey);
        }
      }, 1500);
    } catch (err) {
      console.error('Logout error:', err.response?.data || err.message);
      toast.error('Logout failed');
    }
  };

  return (
    <header className="relative">
      <Navbar expand="lg" className="bg-white shadow-sm navbar-light sticky-top" style={{ zIndex: 100 }}>
        <Container>
          <Navbar.Brand as={Link} to="/" className="d-flex align-items-center">
            <img src={logo} alt="Logo" width="50" height="50" />
          </Navbar.Brand>

          <Button
            variant="light"
            className="d-lg-none border-0"
            onClick={() => setMobileNavOpen(true)}
          >
            ☰
          </Button>

          {/* Desktop Nav */}
          <Navbar.Collapse id="navbar-nav" className="d-none d-lg-flex justify-content-end">
            <Nav className="align-items-center">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.to;
                return (
                  <Nav.Link
                    key={link.to}
                    as={Link}
                    to={link.to}
                    className={`px-3 ${isActive ? "text-dark fw-semibold" : "text-muted"}`}
                  >
                    {link.label}
                  </Nav.Link>
                );
              })}

              {!userData ? (
                <Button
                  as={Link}
                  to="/sign-in"
                  variant="primary"
                  className="rounded-pill px-4 py-2 ms-3"
                >
                  Log in
                </Button>
              ) : (
                <Dropdown align="end">
                  <Dropdown.Toggle variant="link" className="pl-5 text-decoration-none border-0">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: profileColor || '#cccccc',
                        color: "white",
                        fontSize: "18px",
                        fontWeight: "bold"
                      }}
                    >
                      {userData.email_address?.charAt(0).toUpperCase()}
                    </div>
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="shadow border-0 p-2" style={{ top: "60px" }}>
                    <Dropdown.Header>
                      <strong>{userData.name}</strong>
                      <div className="text-muted small">{userData.email_address}</div>
                    </Dropdown.Header>
                    <Dropdown.Divider />
                    <Dropdown.Item
                      as={Link}
                      to="/personal"
                      className={location.pathname.startsWith('/personal') ? 'bg-success-subtle' : ''}
                    >
                      My Profile
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handleLogout}>Sign Out</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Mobile Nav Drawer */}
      {mobileNavOpen && (
        <div className="mobile-nav-overlay position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 zindex-modal">
          <div
            ref={navRef}
            className="mobile-drawer bg-white position-fixed top-0 start-0 h-100 d-flex flex-column"
            style={{ width: "75%", maxWidth: 280, zIndex: 1051 }}
          >
            <div className="d-flex justify-content-end px-3 py-3">
              <Button variant="link" className="text-decoration-none text-dark fs-3 p-0" onClick={() => setMobileNavOpen(false)}>
                &lt;
              </Button>
            </div>

            {/* Main Navigation Links */}
            <Nav className="flex-column px-3 flex-grow-1">
              {navLinks.map((link) => (
                <Nav.Link
                  key={link.to}
                  as={Link}
                  to={link.to}
                  onClick={() => setMobileNavOpen(false)}
                  className={`py-2 ${location.pathname === link.to ? "fw-semibold text-dark" : "text-muted"}`}
                >
                  {link.label}
                </Nav.Link>
              ))}
            </Nav>

            {/* User Section at Bottom */}
            <div className="border-top p-3">
              {!userData ? (
                <Button
                  as={Link}
                  to="/sign-in"
                  variant="primary"
                  className="rounded-pill w-100"
                  onClick={() => setMobileNavOpen(false)}
                >
                  Log in
                </Button>
              ) : (
                <div className="d-flex flex-column">
                  <div className="d-flex align-items-center mb-3">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle me-2"
                      style={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: profileColor || '#cccccc',
                        color: "white",
                        fontSize: "18px",
                        fontWeight: "bold"
                      }}
                    >
                      {userData.email_address?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="fw-semibold">{userData.name}</div>
                      <div className="text-muted small">{userData.email_address}</div>
                    </div>
                  </div>
                  <Nav className="flex-column">
                    <Nav.Link
                      as={Link}
                      to="/personal"
                      onClick={() => setMobileNavOpen(false)}
                      className={`px-0 py-2 text-dark ${location.pathname.startsWith('/personal') ? 'bg-success-subtle' : ''}`}
                    >
                      My Profile
                    </Nav.Link>
                    <Nav.Link
                      onClick={() => {
                        handleLogout();
                        setMobileNavOpen(false);
                      }}
                      className="px-0 py-2 text-danger"
                    >
                      Sign Out
                    </Nav.Link>
                  </Nav>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </header>
  );
};

export default Header;